import React, { useEffect, useMemo, useState } from 'react';
import Modal from './Modal';
import { useAuth } from '../helpers/AuthContext';
import { LOCALE_NAMES, LOCALE_OPTIONS, normalizeLocale } from '../helpers/locales';
import './TranslatableField.css';

function getPath(object, path) {
    return path.split('.').reduce((value, part) => (
        value && typeof value === 'object' ? value[part] : undefined
    ), object);
}

export default function TranslatableField({
    endpoint,
    field,
    label,
    sourceValue = '',
    defaultLanguage = 'en',
    inputType = 'text',
    maxLength,
    translations: initialTranslations = null,
    onTranslationsChange,
}) {
    const auth = useAuth();
    const fieldId = field.replace(/\./g, '-');
    const [isOpen, setIsOpen] = useState(false);
    const [translations, setTranslations] = useState(initialTranslations);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [locale, setLocale] = useState('');
    const [value, setValue] = useState('');
    const [editingLocale, setEditingLocale] = useState(null);

    useEffect(() => {
        setTranslations(initialTranslations || null);
    }, [initialTranslations]);

    const apiEndpoint = `${auth.API_URL}${endpoint.replace(/\/$/, '')}`;
    const normalizedDefault = normalizeLocale(defaultLanguage);
    const entries = useMemo(() => Object.entries(translations || {})
        .map(([language, translation]) => ({
            locale: normalizeLocale(language),
            localeName: LOCALE_NAMES[normalizeLocale(language)],
            value: getPath(translation, field),
        }))
        .filter(entry => typeof entry.value !== 'undefined')
        .sort((left, right) => left.locale.localeCompare(right.locale)), [translations, field]);
    const localeOptions = useMemo(() => {
        const options = LOCALE_OPTIONS.filter(([code]) => code !== normalizedDefault);
        if (locale && !options.some(([code]) => code === locale)) {
            return [[locale, locale], ...options];
        }
        return options;
    }, [locale, normalizedDefault]);

    const updateTranslations = nextTranslations => {
        setTranslations(nextTranslations || null);
        onTranslationsChange?.(nextTranslations || null);
    };

    async function request(url, options = {}) {
        const response = await auth.fetch(url, {
            ...options,
            headers: {
                Accept: 'application/json',
                ...(options.body ? { 'Content-Type': 'application/json' } : {}),
                ...(options.headers || {}),
            },
        });
        const json = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(json.error || 'Translation request failed');
        return json.data || {};
    }

    async function openEditor() {
        setIsOpen(true);
        setLoading(true);
        setError(null);
        try {
            const data = await request(apiEndpoint);
            updateTranslations(data.translations);
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setLoading(false);
        }
    }

    function resetForm() {
        setLocale('');
        setValue('');
        setEditingLocale(null);
    }

    function editTranslation(entry) {
        setLocale(entry.locale);
        setValue(entry.value);
        setEditingLocale(entry.locale);
        setError(null);
    }

    async function saveTranslation(event) {
        event.preventDefault();
        const normalizedLocale = normalizeLocale(locale);
        if (!normalizedLocale) {
            setError('Enter a locale code.');
            return;
        }
        if (normalizedLocale === normalizedDefault) {
            setError('Edit the base field to change the default language value.');
            return;
        }

        setSaving(true);
        setError(null);
        try {
            const data = await request(`${apiEndpoint}/${encodeURIComponent(normalizedLocale)}`, {
                method: 'PUT',
                body: JSON.stringify({ field, value }),
            });
            updateTranslations(data.translations);
            resetForm();
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setSaving(false);
        }
    }

    async function deleteTranslation(language) {
        if (!window.confirm(`Remove the ${language} translation for ${label}?`)) return;
        setSaving(true);
        setError(null);
        try {
            const query = new URLSearchParams({ field });
            const data = await request(
                `${apiEndpoint}/${encodeURIComponent(language)}?${query}`,
                { method: 'DELETE' }
            );
            updateTranslations(data.translations);
            if (editingLocale === language) resetForm();
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setSaving(false);
        }
    }

    async function deleteAllTranslations() {
        if (!window.confirm(`Remove all translations for ${label}?`)) return;
        setSaving(true);
        setError(null);
        try {
            const query = new URLSearchParams({ field });
            const data = await request(`${apiEndpoint}?${query}`, { method: 'DELETE' });
            updateTranslations(data.translations);
            resetForm();
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setSaving(false);
        }
    }

    const inputProps = {
        id: `translation-${fieldId}-value`,
        value,
        maxLength,
        onChange: event => setValue(event.target.value),
        disabled: saving,
    };

    return (
        <>
            <button
                type="button"
                className={`translatable-field-trigger${entries.length ? ' has-translations' : ''}`}
                onClick={openEditor}
                aria-label={`Manage translations for ${label}`}
                title={`Manage translations for ${label}`}
            >
                <i className="fa-solid fa-language" aria-hidden="true" />
                {entries.length ? (
                    <span className="translatable-field-count">{entries.length}</span>
                ) : null}
            </button>
            <Modal
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                title={`Translations for ${label}`}
                className="translatable-field-modal"
                footer={(
                    <button
                        type="button"
                        className="button secondary"
                        onClick={() => setIsOpen(false)}
                    >
                        Close
                    </button>
                )}
            >
                <div className="translatable-field-source">
                    <strong>Default ({normalizedDefault || 'default'})</strong>
                    <div>{String(sourceValue || '') || <em>Empty</em>}</div>
                </div>

                <p className="help translatable-field-availability-hint">
                    Only translations for languages enabled under Storefront Settings →
                    Customization are shown to visitors. Other translations remain saved but hidden.
                </p>

                {loading ? <p role="status">Loading translations...</p> : null}
                {error ? <div className="error" role="alert">{error}</div> : null}

                {!loading && entries.length ? (
                    <div className="translatable-field-list">
                        {entries.map(entry => (
                            <div className="translatable-field-row" key={entry.locale}>
                                <div>
                                    <strong>
                                        {entry.localeName
                                            ? `${entry.localeName} (${entry.locale})`
                                            : entry.locale}
                                    </strong>
                                    <div>{entry.value || <em>Empty</em>}</div>
                                </div>
                                <div className="translatable-field-actions">
                                    <button
                                        type="button"
                                        className="button secondary small"
                                        onClick={() => editTranslation(entry)}
                                        disabled={saving}
                                    >
                                        Edit
                                    </button>
                                    <button
                                        type="button"
                                        className="button secondary small"
                                        onClick={() => deleteTranslation(entry.locale)}
                                        disabled={saving}
                                        aria-label={`Remove ${entry.locale} translation`}
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : null}

                {!loading ? (
                    <form className="translatable-field-form" onSubmit={saveTranslation}>
                        <h3>{editingLocale ? `Edit ${editingLocale}` : 'Add a translation'}</h3>
                        <div className="group">
                            <label htmlFor={`translation-${fieldId}-locale`}>Locale code</label>
                            <select
                                id={`translation-${fieldId}-locale`}
                                value={locale}
                                onChange={event => setLocale(event.target.value)}
                                disabled={saving || !!editingLocale}
                                required
                            >
                                <option value="">Select a language</option>
                                {localeOptions.map(([code, name]) => (
                                    <option value={code} key={code}>{name} ({code})</option>
                                ))}
                            </select>
                            <p className="help">
                                Select a language other than the default language.
                            </p>
                        </div>
                        <div className="group">
                            <label htmlFor={inputProps.id}>Translated value</label>
                            {inputType === 'textarea'
                                ? <textarea {...inputProps} rows={4} />
                                : <input {...inputProps} type="text" />}
                        </div>
                        <div className="translatable-field-form-actions">
                            <button type="submit" className="button primary" disabled={saving}>
                                {saving ? 'Saving...' : 'Save translation'}
                            </button>
                            {editingLocale ? (
                                <button
                                    type="button"
                                    className="button secondary"
                                    onClick={resetForm}
                                    disabled={saving}
                                >
                                    Cancel edit
                                </button>
                            ) : null}
                        </div>
                    </form>
                ) : null}

                {!loading && entries.length ? (
                    <a
                        href="#"
                        className="translatable-field-delete-all"
                        onClick={event => {
                            event.preventDefault();
                            if (!saving) deleteAllTranslations();
                        }}
                        aria-disabled={saving}
                    >
                        Remove all translations for this field
                    </a>
                ) : null}
            </Modal>
        </>
    );
}
